/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import cx from 'classnames';
import azCommonStyles from '../../../theme/globals.module.scss';
import { Button as IncDecButton } from '@/components/Button';
import {
  quantityComponent,
  quantityComponentBrazil,
} from '../../../constants/images/quantityComponent';
import { keyCodes } from '../../../constants/keyCodes';
import styles from './styles.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { routePaths } from '@/constants/routePaths';
import Image from '@/components/NextImage';
import { useFeatureFlag } from '@/features/globalConfig';
import { countryCodes } from '@/constants/locale';
import { generateAriaLabelsForQtyStepper } from '@/features/cart';

const labelMap = {
  lbl: 'label_shelf_resultsList_price_totalPrice',
  lblDecrease: 'label_Decrease',
  lblIncrease: 'label_Increase',
  lblStepperAria: 'label_cart_item_qty_stepper_aria',
};

type Props = {
  count: number | string;
  maxLength?: number;
  onInputChange: (b: number, a: boolean) => void;
  handleZeroInput?: () => Promise<void>;
  onIncrementDecrement: (count: number, requireAnalytics: boolean, showQtyError?: boolean) => void;
  hasError?: boolean;
  minQuantity?: number;
  disableIncrementBtn?: boolean;
  moreMargin?: boolean;
  isIncOrDecBtnTouched?: boolean;
  disabled?: boolean;
  maxQuantity?: number;
  filterproduct?: any;
  productName: string;
};

export const handleKeyDown = async (
  event: any,
  count: number | string,
  handleZeroInput?: () => Promise<void>,
) => {
  if ((event.keyCode === keyCodes.enter && count === 0) || count === '') {
    await handleZeroInput?.();
  }
};

export const handleOnBlur = async (
  count: string | number,
  handleZeroInput?: () => Promise<void>,
) => {
  if (count === 0 || count === '') {
    await handleZeroInput?.();
  }
};

export const handleKeyPress = (
  event: any,
  onInputChange: Function,
  hasError: boolean,
  maxQuantity?: number,
): any | void => {
  const numberPattern = /\d/g;
  const valueTest = numberPattern.test(event.target.value);
  if (maxQuantity !== null && maxQuantity !== undefined && maxQuantity === 0) {
    return onInputChange(0, false, hasError);
  }
  if (valueTest || event.target.value === '0' || event.target.value === '') {
    if (!isNaN(event.target.value)) {
      return onInputChange(Number(event.target.value), false, hasError);
    }
  }
};

export const QuantityComponent = (props: Props) => {
  const {
    onIncrementDecrement,
    count,
    minQuantity = 1,
    hasError = false,
    onInputChange,
    maxLength = 2,
    disableIncrementBtn = false,
    moreMargin = false,
    isIncOrDecBtnTouched,
    handleZeroInput,
    maxQuantity,
    disabled = false,
    filterproduct,
    productName,
  } = props;
  const normalizedCount = Math.abs(Number(count));
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const {
    increaseSvg: { alt: increaseAlt, src: increaseSrc },
    decreaseSvg: { alt: decreaseAlt, src: decreaseSrc },
  } = locale === countryCodes.ptBr ? quantityComponentBrazil : quantityComponent;

  const { increaseAriaLabel, decreaseAriaLabel } = React.useMemo(() => {
    return {
      increaseAriaLabel: generateAriaLabelsForQtyStepper({
        template: labels.lblStepperAria,
        productName,
        action: labels.lblIncrease,
        quantity: normalizedCount,
      }),
      decreaseAriaLabel: generateAriaLabelsForQtyStepper({
        template: labels.lblStepperAria,
        productName,
        action: labels.lblDecrease,
        quantity: normalizedCount,
      }),
    };
  }, [labels, productName, normalizedCount]);

  const router = useLegacyRouter();
  const isCartPage = router.asPath === routePaths.cart;

  const decrementAmount: number = maxQuantity
    ? Number(count) > maxQuantity
      ? maxQuantity
      : Number(count) - 1
    : Number(count) - 1;

  const disableDecrementBtn = (): boolean => {
    const countLessThanMin = Number(count) <= minQuantity;
    let storeHasNoQoh: boolean = false;
    if (maxQuantity !== undefined) {
      storeHasNoQoh = maxQuantity <= 0;
    }
    return countLessThanMin || storeHasNoQoh;
  };

  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  let showStrikeThroughPricing = false;
  if (strikeThroughPricingEnabled && filterproduct?.skuPricingAndAvailability?.discountedPrice) {
    showStrikeThroughPricing = true;
  }

  return (
    <div
      data-testid="quantity-component"
      className={cx(styles.quantityMainContainer, {
        [styles.cartPageQuantityContainer]: isCartPage && locale !== 'es-MX',
      })}
    >
      <div className={styles.quantityContainer}>
        <IncDecButton
          customClass={styles.round}
          data-testid="quantity-decrement"
          value=""
          disabled={disableDecrementBtn()}
          onClick={() => {
            onIncrementDecrement(decrementAmount, true, hasError);
          }}
          aria-label={decreaseAriaLabel}
        >
          <Image src={decreaseSrc} alt={decreaseAlt} width={10} height={3} />
        </IncDecButton>
        <input
          name="itemQuantity"
          data-testid="quantityInput"
          aria-label="Product Quantity"
          type="text"
          onBlur={async () => await handleOnBlur(count, handleZeroInput)}
          inputMode="numeric"
          className={cx(
            azCommonStyles['center-align'],
            azCommonStyles['az-title-5-medium'],
            styles.qtyTextBox,
            hasError && isIncOrDecBtnTouched ? styles.errorInput : styles.normalInput,
            !moreMargin
              ? showStrikeThroughPricing
                ? styles.inputStyleStrikeThrough
                : styles.inputStyle
              : styles.inputStyleMoreMargin,
          )}
          value={normalizedCount}
          onChange={(evt) => handleKeyPress(evt, onInputChange, hasError, maxQuantity)}
          onKeyDown={async (evt) => await handleKeyDown(evt, count, handleZeroInput)}
          maxLength={maxLength}
          disabled={disabled}
        />
        <IncDecButton
          customClass={styles.round}
          data-testid="quantity-increment"
          onClick={() => {
            onIncrementDecrement(Number(count) + 1, true, hasError);
          }}
          disabled={disableIncrementBtn}
          aria-label={increaseAriaLabel}
        >
          <Image src={increaseSrc} alt={increaseAlt} width={10} height={11} />
        </IncDecButton>
      </div>
    </div>
  );
};

export default QuantityComponent;
